import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { INDUSTRIES } from "@/lib/site";
import { INDUSTRY_OG_CARDS, absoluteOg } from "@/lib/ogCards";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Home as HomeIcon,
  Briefcase,
  Hotel,
  GraduationCap,
  Landmark,
  Factory,
  Warehouse,
  Trees,
  Building2,
  type LucideIcon,
} from "lucide-react";

type Detail = {
  tagline: string;
  overview: string;
  stats: { value: string; label: string }[];
  pains: string[];
  strategy: string;
  capabilities: { title: string; description: string }[];
  uses: string[];
  mistakes: string[];
};

const DETAILS: Record<string, Detail> = {
  "luxury-residences": {
    tagline: "Architecturally invisible. Operationally effortless.",
    overview:
      "Private estates demand technology that disappears into the architecture while protecting the family, the collection, and the experience. Every interface is designed for the owner — not the operator.",
    stats: [
      { value: "120+", label: "Private Residences" },
      { value: "9.4.6", label: "Reference Atmos Cinemas" },
      { value: "0", label: "Visible Compromises" },
    ],
    pains: [
      "Visible cameras compromise architecture",
      "Disparate brand apps frustrate owners",
      "Poor cinema and audio design",
    ],
    strategy:
      "Discrete, architecturally-integrated security with unified automation and reference-grade cinema.",
    capabilities: [
      { title: "Estate Perimeter", description: "Discreet detection across boundary walls, gates and grounds." },
      { title: "Unified Automation", description: "Lutron, KNX and Crestron under one owner interface." },
      { title: "Private Cinema", description: "Acoustically engineered 9.4.6 Atmos reference rooms." },
      { title: "Distributed Audio", description: "High-fidelity zones across living, outdoor and wellness areas." },
    ],
    uses: ["Perimeter & access for estate", "Lutron / KNX automation", "Private 9.4.6 Atmos theatre"],
    mistakes: [
      "Choosing automation brand before architecture and interior design",
      "Visible cameras and exposed conduits compromising elevation",
      "Cinema specified after room dimensions and HVAC are frozen",
      "Separate apps for lighting, climate, security and cinema",
      "No serviceability plan for rack, AV and automation equipment rooms",
    ],
  },
  "farmhouses-private-estates": {
    tagline: "Acres to secure. Owners to delight.",
    overview:
      "Farmhouses and weekend estates blend long perimeters with intimate living spaces. The technology must protect a sprawling boundary while keeping the family experience effortless — automation, cinema, audio and security under one calm interface.",
    stats: [
      { value: "5–25", label: "Acre Estates" },
      { value: "1 App", label: "Owner Interface" },
      { value: "24/7", label: "Remote Visibility" },
    ],
    pains: [
      "Long, unlit perimeters with blind spots",
      "Caretaker-dependent security with no audit trail",
      "Owner away most of the week — no remote visibility",
    ],
    strategy:
      "Layered perimeter detection across boundaries, integrated automation for the main house, and a single owner app for security, climate, lighting and cinema.",
    capabilities: [
      { title: "Estate Perimeter", description: "Thermal + analytics across boundary walls, gates and grounds with low false-alarm rates." },
      { title: "Gate & Visitor Control", description: "ANPR, video door phone and pre-registered guest entry — staff and family separated." },
      { title: "Whole-Estate Automation", description: "Lighting, shading, climate, irrigation and audio on one platform with offline fallbacks." },
      { title: "Private Cinema & Audio", description: "Reference 7.2.4 / 9.4.6 Atmos rooms and weatherised outdoor audio zones." },
    ],
    uses: ["Perimeter detection across acres", "Owner-app for everything", "Outdoor audio + private cinema"],
    mistakes: [
      "Treating the boundary wall as security on its own",
      "No remote video verification when the owner is away",
      "Separate apps for caretaker, security and automation",
      "Outdoor speakers and cameras not weatherised for monsoon",
      "No standby power plan for gates, cameras and the control rack",
    ],
  },
  "developers-real-estate": {
    tagline: "Sales-ready technology. Handover-ready operations.",
    overview:
      "Developers win on two fronts: a sales experience that signals premium, and a handover that an owners' association can actually run. We help projects specify, value-engineer and standardise technology so what's sold matches what's installed — and what's installed can be operated for decades.",
    stats: [
      { value: "20+", label: "Residential Projects" },
      { value: "1 BoQ", label: "Per Project Type" },
      { value: "10 yr", label: "Operations Horizon" },
    ],
    pains: [
      "Sales spec doesn't match what's actually delivered",
      "Common-area systems handed over with no operating model",
      "Per-tower BOQs differ — no portfolio standard",
    ],
    strategy:
      "Standardised technology BOQs across towers and projects, sample-flat reference designs, and a documented handover pack for the owners' association.",
    capabilities: [
      { title: "Specification & BOQ", description: "Apartment, common-area and clubhouse technology specs that bid cleanly and install consistently." },
      { title: "Sample Flat Reference", description: "A working sample flat that proves the experience promised in sales collateral." },
      { title: "Common-Area Systems", description: "CCTV, access, boom barriers, ANPR, fire and PA designed as one integrated stack." },
      { title: "Handover & Operations", description: "AMC structure, operator training and documentation packs for the RWA / FM team." },
    ],
    uses: ["Project-wide technology BOQ", "Sample flat reference fit-out", "Common-area + clubhouse stack"],
    mistakes: [
      "Sales brochure promising 'smart' without a defined technology BOQ",
      "Each tower procured separately — three brands of CCTV in one project",
      "Clubhouse AV specified after interior works are frozen",
      "No documented handover to the RWA or facility manager",
      "Boom barriers and ANPR under-specified for peak hour cycle count",
    ],
  },
  "corporate-offices": {
    tagline: "Portfolio-standard AV. Unified security policy.",
    overview:
      "Modern workplaces need consistent meeting-room UX across every floor, every city — paired with a single access and surveillance policy that survives expansion, renovation and reorganisation.",
    stats: [
      { value: "200+", label: "Meeting Rooms Standardised" },
      { value: "12", label: "Multi-Floor HQs" },
      { value: "1", label: "Policy Framework" },
    ],
    pains: [
      "Inconsistent meeting-room UX",
      "Visitor & access friction",
      "Security policies fragmented across floors",
    ],
    strategy: "Portfolio-standard AV plus unified physical-security policy and visitor flow.",
    capabilities: [
      { title: "Room Reference Designs", description: "Huddle, mid, large and boardroom — one experience." },
      { title: "Touchless Access", description: "Mobile credentials, visitor pre-registration, frictionless entry." },
      { title: "Integrated Analytics", description: "Occupancy, tailgating and incident review across floors." },
      { title: "Network Backbone", description: "Converged IP for AV, security and IoT at scale." },
    ],
    uses: ["Standard room reference designs", "Touchless access", "Integrated video analytics"],
    mistakes: [
      "Different AV experiences in every meeting room",
      "Access control deployed door-by-door without a central policy engine",
      "Visitor process still on paper after spending on automation",
      "Network and AV procured separately, then expected to integrate",
      "No serviceability or training plan for facilities and IT operators",
    ],
  },
  hospitality: {
    tagline: "Invisible to guests. Indispensable to operations.",
    overview:
      "Luxury hospitality runs on two parallel tracks: a seamless guest journey and a precise back-of-house operation. Technology must elevate both without ever becoming visible friction.",
    stats: [
      { value: "40+", label: "Properties Delivered" },
      { value: "5★", label: "Brand Standards" },
      { value: "24/7", label: "Banquet Readiness" },
    ],
    pains: [
      "Guest experience tension with security",
      "Banquet AV complexity",
      "Network sprawl",
    ],
    strategy: "Invisible guest security, scalable banquet AV, converged IP networks.",
    capabilities: [
      { title: "Discreet Surveillance", description: "Coverage that respects guest privacy and brand aesthetics." },
      { title: "Banquet & Ballroom AV", description: "Reconfigurable for weddings, conferences and galas." },
      { title: "Spa & Cinema", description: "Wellness audio and private screening experiences." },
      { title: "Property Networks", description: "Resilient, segmented IP across BOH and guest-facing systems." },
    ],
    uses: ["Discreet surveillance", "Scalable ballroom AV", "Spa & cinema integrations"],
    mistakes: [
      "Security hardware that visibly conflicts with brand aesthetic",
      "Banquet AV procured per-event instead of as a permanent system",
      "Guest Wi-Fi and back-of-house networks not segmented",
      "Spa, cinema and in-room audio left to multiple vendors",
      "No central command for incident response across the property",
    ],
  },
  "education-auditoriums": {
    tagline: "Speech-intelligible halls. One-touch operation.",
    overview:
      "Auditoriums and lecture halls succeed when every seat hears clearly, every session captures cleanly, and every operator can run the room without a manual.",
    stats: [
      { value: "60+", label: "Halls Engineered" },
      { value: "2000", label: "Largest Seat Count" },
      { value: "1-Touch", label: "Operator UX" },
    ],
    pains: [
      "Speech intelligibility issues",
      "Hybrid teaching gaps",
      "Operator complexity",
    ],
    strategy: "Acoustic-first auditorium design with hybrid-ready capture and one-touch operation.",
    capabilities: [
      { title: "Acoustic Engineering", description: "Room modelling, treatment and loudspeaker design from first principles." },
      { title: "Lecture Capture", description: "Multi-camera, auto-tracking, streaming-ready." },
      { title: "Stage & House", description: "Lighting, rigging and AV control under a single console." },
      { title: "Campus Security", description: "Backbone connecting halls, hostels and gates." },
    ],
    uses: ["500–2000 seat halls", "Lecture-capture & streaming", "Campus security backbone"],
    mistakes: [
      "Loudspeakers and acoustics treated as a post-fit-out purchase",
      "Stage lighting and AV procured without operator workflow in mind",
      "Hybrid capture and streaming added after commissioning",
      "Control rooms designed without sightlines to stage or screen",
      "Campus security and auditorium AV treated as unrelated programs",
    ],
  },
  "government-infrastructure": {
    tagline: "Defence-in-depth. Audit-ready command.",
    overview:
      "Critical infrastructure requires layered detection, hardened command-and-control, and compliance posture that holds up under scrutiny — across districts, states and decades.",
    stats: [
      { value: "500km+", label: "Perimeter Secured" },
      { value: "30+", label: "Command Centres" },
      { value: "24/7", label: "Operational SLA" },
    ],
    pains: [
      "High-stakes perimeter security",
      "Compliance & data residency",
      "Multi-site command",
    ],
    strategy: "Defence-in-depth perimeter with hardened command-and-control and policy compliance.",
    capabilities: [
      { title: "Sensor-Fused Perimeter", description: "Fibre, radar, thermal and analytics under one correlation engine." },
      { title: "Hardened VMS", description: "On-prem, role-based, audit-logged video management." },
      { title: "Ops Centres", description: "Video-wall, workflow and incident playbook design." },
      { title: "Compliance Posture", description: "Data residency, retention and access governance." },
    ],
    uses: ["Fibre + radar perimeter", "Hardened VMS", "24/7 ops centres"],
    mistakes: [
      "Single-sensor perimeter without correlation or fusion",
      "Cyber-vulnerable IP cameras and default credentials",
      "Data residency and retention policy decided after deployment",
      "Command centre built without SOPs or operator playbooks",
      "Vendor lock-in on closed VMS that blocks future analytics",
    ],
  },
  "industrial-facilities": {
    tagline: "Long perimeters. Zero false alarms.",
    overview:
      "Refineries, plants and yards stretch for kilometres in harsh conditions. Security and safety must converge in operations rooms that act, not just observe.",
    stats: [
      { value: "80+", label: "Industrial Sites" },
      { value: "200km", label: "Plant Perimeter" },
      { value: "<2%", label: "False Alarm Rate" },
    ],
    pains: [
      "Long perimeters across harsh terrain",
      "Theft & intrusion",
      "Safety + security overlap",
    ],
    strategy: "Sensor-fused perimeter + integrated safety & security operations.",
    capabilities: [
      { title: "Thermal + Analytics", description: "All-weather detection with AI-classified intrusion alerts." },
      { title: "Gate-Level Access", description: "Vehicle and personnel control at every entry." },
      { title: "ANPR & Vehicle ID", description: "Yard, weighbridge and dispatch automation." },
      { title: "Unified Ops Room", description: "Safety, security and process events in one workflow." },
    ],
    uses: ["Thermal + analytics", "Access at every gate", "Vehicle ID & ANPR"],
    mistakes: [
      "Commercial-grade gates fitted with domestic-grade actuators",
      "Long perimeters covered with cameras alone — no detection layer",
      "Safety and security events split across two operations rooms",
      "Weighbridge, ANPR and dispatch systems left unintegrated",
      "Maintenance access and spare-parts planning ignored at design",
    ],
  },
  "warehouses-logistics": {
    tagline: "Yard-grade analytics. Dispatch-grade visibility.",
    overview:
      "Modern logistics hubs cannot afford blind spots between gate, dock and yard. Security becomes an operational data layer — feeding dispatch, audit and customer commitments.",
    stats: [
      { value: "150+", label: "DCs & Yards" },
      { value: "ANPR", label: "Gate to Dock" },
      { value: "Multi-Site", label: "Command" },
    ],
    pains: [
      "Yard security gaps",
      "Operational visibility",
      "False alarms drain response",
    ],
    strategy: "Yard-grade analytics + ANPR + perimeter with operations-grade dashboards.",
    capabilities: [
      { title: "ANPR & Dock Monitoring", description: "Inbound, outbound and dwell-time intelligence." },
      { title: "Yard Analytics", description: "Trailer tracking, loitering detection, slot management." },
      { title: "Perimeter & Gates", description: "Layered detection with rapid-response workflows." },
      { title: "Multi-Site Command", description: "Roll-up dashboards across regional and national networks." },
    ],
    uses: ["ANPR & dock monitoring", "Yard analytics", "Multi-site command"],
    mistakes: [
      "Gate ANPR procured without dock-scheduling integration",
      "Open yards covered only by perimeter cameras — no analytics",
      "Per-site security with no national roll-up dashboard",
      "Visitor and driver management still paper-based",
      "Boom barriers and gate motors under-specified for cycle count",
    ],
  },
};

const SITE_ORIGIN = "https://arifkhanglobal.com";

type SeoMeta = { title: string; description: string };

const SEO_META: Record<string, SeoMeta> = {
  "luxury-residences": {
    title: "Luxury Residence Security & Automation | Arif Khan",
    description:
      "Discreet estate security, Lutron/KNX/Crestron automation and reference 9.4.6 Atmos cinemas for private residences and villas across India.",
  },
  "farmhouses-private-estates": {
    title: "Farmhouse & Private Estate Security & Automation | Arif Khan",
    description:
      "Long-perimeter security, owner-app automation, outdoor audio and private cinema for farmhouses and weekend estates across Delhi NCR and North India.",
  },
  "developers-real-estate": {
    title: "Technology Consultant for Developers | Arif Khan",
    description:
      "Standardised technology BOQs, sample-flat reference designs and handover-ready common-area systems for residential and mixed-use developers.",
  },
  "corporate-offices": {
    title: "Corporate Office AV & Security Integrator | Arif Khan",
    description:
      "Portfolio-standard meeting rooms, touchless access and unified surveillance for multi-floor corporate HQs and global capability centres.",
  },
  hospitality: {
    title: "Hospitality AV, Banquet Sound & Hotel Security | Arif Khan",
    description:
      "Invisible guest security, scalable banquet AV and converged IP networks for luxury hotels, resorts and serviced residences.",
  },
  "education-auditoriums": {
    title: "Auditorium AV, Lecture Capture & Campus Security | Arif Khan",
    description:
      "Acoustic-first 500–2000 seat halls with hybrid-ready capture, one-touch operation and campus-wide security backbones.",
  },
  "government-infrastructure": {
    title: "Government Perimeter Security & Command Centres | Arif Khan",
    description:
      "Defence-in-depth fibre, radar and thermal perimeters with hardened VMS and 24/7 operations centres for critical infrastructure.",
  },
  "industrial-facilities": {
    title: "Industrial Perimeter, ANPR & Safety Integration | Arif Khan",
    description:
      "Sensor-fused thermal perimeters, gate-level access and unified safety/security ops rooms for refineries, plants and yards.",
  },
  "warehouses-logistics": {
    title: "Warehouse Security, ANPR & Yard Analytics | Arif Khan",
    description:
      "ANPR, dock monitoring, yard analytics and multi-site command dashboards for distribution centres and logistics networks.",
  },
};

const FAQS: Record<string, { q: string; a: string }[]> = {
  "luxury-residences": [
    { q: "Can security cameras be hidden in a luxury residence?", a: "Yes. We specify miniature, architecturally-flush optics and route cabling inside finishes during civil works so coverage is complete without visible hardware." },
    { q: "Which home automation platforms do you work with?", a: "Lutron, KNX and Crestron — unified under a single owner interface that controls lighting, climate, shades, audio, cinema and security." },
    { q: "What does a reference-grade private cinema involve?", a: "Acoustic room modelling, 9.4.6 Dolby Atmos speaker layouts, calibrated projection and isolation treatment engineered before construction begins." },
  ],
  "corporate-offices": [
    { q: "How do you standardise AV across multiple floors and cities?", a: "We publish reference designs for huddle, mid, large and boardroom typologies so every site delivers the same user experience and BOM." },
    { q: "What does touchless access control look like?", a: "Mobile credentials, visitor pre-registration and frictionless turnstiles tied into a single policy and audit trail across the portfolio." },
    { q: "Can existing surveillance be unified under one VMS?", a: "Yes — we consolidate legacy DVRs and brand silos onto an enterprise VMS with role-based access and cross-floor analytics." },
  ],
  hospitality: [
    { q: "How do you secure guests without compromising experience?", a: "Discreet optics in public areas, privacy-respecting coverage and back-of-house intelligence that surfaces incidents to operations only." },
    { q: "Can banquet AV reconfigure for different events?", a: "Yes. Modular line-array, wireless mics, projection and lighting presets allow weddings, conferences and galas to run from one operator console." },
    { q: "Do you integrate spa, cinema and in-room audio?", a: "We deliver high-fidelity distributed audio for wellness, private screening rooms and presidential suites with central management." },
  ],
  "education-auditoriums": [
    { q: "How do you guarantee speech intelligibility in large halls?", a: "Room acoustic modelling, treatment and loudspeaker design from first principles — STI targets verified on commissioning." },
    { q: "Is lecture capture and streaming included?", a: "Yes — multi-camera auto-tracking, recording and live streaming integrated with the institution's LMS." },
    { q: "Can one operator run the entire room?", a: "A single touch panel controls stage lighting, audio, projection, capture and house lights, with presets per session type." },
  ],
  "government-infrastructure": [
    { q: "What perimeter technologies do you deploy?", a: "Fibre intrusion, ground/perimeter radar, thermal cameras and AI analytics — correlated through a single command engine to suppress false alarms." },
    { q: "How is data residency and compliance handled?", a: "On-premise hardened VMS, role-based access, full audit logging and retention policies aligned to sectoral compliance requirements." },
    { q: "Can you operate multi-site command centres 24/7?", a: "Yes — we design video-wall layouts, workflows and incident playbooks for round-the-clock operations across districts and states." },
  ],
  "industrial-facilities": [
    { q: "How do you keep false alarm rates low across long perimeters?", a: "AI classification on thermal feeds, sensor fusion and rule-based correlation hold field-measured false alarm rates below 2%." },
    { q: "Do you integrate safety and security in one ops room?", a: "Yes — process, safety and security events are unified in a single workflow so responders see one prioritised picture." },
    { q: "Can vehicle access at every gate be automated?", a: "ANPR, RFID and driver authentication at gates feed weighbridge, yard and dispatch systems for end-to-end visibility." },
  ],
  "warehouses-logistics": [
    { q: "What does ANPR add to a distribution centre?", a: "Inbound/outbound timestamps, dwell time, dock allocation and exception alerts feed both security and operations dashboards." },
    { q: "How do you secure large open yards?", a: "Layered perimeter detection plus yard analytics (trailer tracking, loitering, slot occupancy) with rapid-response workflows." },
    { q: "Can you provide a national multi-site command view?", a: "Yes — regional and national roll-up dashboards consolidate KPIs, incidents and uptime across DCs and yards." },
  ],
  "farmhouses-private-estates": [
    { q: "How do you secure a farmhouse perimeter that isn't fully walled?", a: "Layered detection — thermal cameras with AI analytics on the boundary, beam/fence sensors on vulnerable stretches, and verification cameras at every gate, correlated in one app with low false-alarm tuning." },
    { q: "Can the owner see and control the estate from the city?", a: "Yes. One app on the owner's phone shows live cameras, gate events, automation status and climate — with the same controls available to a verified caretaker." },
    { q: "Do you handle outdoor audio and lighting design too?", a: "Yes — weatherised outdoor audio zones for poolside and lawns, façade and landscape lighting scenes, and irrigation/HVAC integration tied into the same control platform." },
  ],
  "developers-real-estate": [
    { q: "When should a developer bring in a technology consultant?", a: "At concept and design-development — before BOQs are frozen. That's when standardised specs for apartments, common areas and clubhouses save the most cost and avoid mis-sold features." },
    { q: "What does a sample-flat reference design include?", a: "A working specification of lighting, automation, AV, CCTV, video door phone and structured cabling — installed in one sample flat so buyers experience the exact spec that ships portfolio-wide." },
    { q: "Do you support handover to the RWA or facility manager?", a: "Yes — documentation packs, operator training, AMC structure and SOPs so common-area CCTV, access, fire and boom-barrier systems remain operable for years after handover." },
  ],
};

const INDUSTRY_ICONS: Record<string, LucideIcon> = {
  "luxury-residences": HomeIcon,
  "farmhouses-private-estates": Trees,
  "developers-real-estate": Building2,
  "corporate-offices": Briefcase,
  hospitality: Hotel,
  "education-auditoriums": GraduationCap,
  "government-infrastructure": Landmark,
  "industrial-facilities": Factory,
  "warehouses-logistics": Warehouse,
};

export const Route = createFileRoute("/industries/$slug")({
  loader: ({ params }) => {
    const industry = INDUSTRIES.find((i) => i.slug === params.slug);
    if (!industry) throw notFound();
    return {
      industry,
      detail: DETAILS[params.slug],
      seo: SEO_META[params.slug],
      faqs: FAQS[params.slug] ?? [],
    };
  },
  component: IndustryPage,
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { industry, detail, seo, faqs } = loaderData;
    const url = `${SITE_ORIGIN}/industries/${params.slug}`;
    const image = absoluteOg(INDUSTRY_OG_CARDS[params.slug], industry.icon);
    const title = seo?.title ?? `${industry.title} — Industries | Arif Khan`;
    const description = seo?.description ?? `${detail.tagline} ${detail.overview}`.slice(0, 158);

    const faqSchema = faqs.length
      ? {
          "@type": "FAQPage",
          "@id": `${url}#faq`,
          inLanguage: "en-IN",
          isPartOf: { "@id": `${url}#webpage` },
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1216" },
        { property: "og:image:height", content: "640" },
        { property: "og:image:alt", content: `${industry.title} — Arif Khan` },

        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", type: "text/markdown", href: `${url}.md`, title: "Markdown mirror" },
      ],
      scripts: [
        pageSchemaScript(
          {
            path: `/industries/${params.slug}`,
            type: "WebPage",
            name: title,
            description,
            breadcrumb: [
              { name: "Industries", url: "/industries" },
              { name: industry.title, url: `/industries/${params.slug}` },
            ],
            extra: { about: { "@id": PERSON_ID } },
          },
          faqSchema ? [faqSchema] : [],
        ),
      ],
    };
  },
});

function IndustryPage() {
  const { industry, detail, faqs } = Route.useLoaderData() as {
    industry: { slug: string; title: string; icon: string };
    detail: Detail;
    faqs: { q: string; a: string }[];
  };
  const Icon = INDUSTRY_ICONS[industry.slug] ?? ShieldCheck;

  return (
    <PageShell>
      {/* Tailored hero */}
      <section className="relative pt-40 pb-24 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `url(${industry.icon})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(40px) saturate(1.1)",
            transform: "scale(1.15)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/85 to-surface/60" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
        <div className="container-page relative grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <span className="eyebrow">Industries</span>
              <span className="font-mono text-[10px] text-ink-soft tracking-widest">/ {industry.title}</span>
            </div>
            <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-xl glass-dark text-white">
              <Icon className="h-5 w-5" strokeWidth={1.4} />
            </div>
            <h1 className="mt-6 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95]">
              {industry.title}
            </h1>
            <p className="mt-6 max-w-2xl text-pretty text-xl text-ink-soft leading-relaxed">
              {detail.tagline}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border-strong/40 shadow-elevated">
              <img
                src={industry.icon}
                alt={`${industry.title} — representative sector environment for security, automation and AV systems`}
                width={1200}
                height={900}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Sector overview + stats */}
      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Sector Overview" title="What this sector demands." />
          </div>
          <div className="lg:col-span-7">
            <p className="text-xl text-foreground leading-relaxed text-pretty">{detail.overview}</p>
            <div className="mt-10 grid grid-cols-3 gap-5">
              {detail.stats.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.05}>
                  <div className="rounded-xl border border-border bg-surface p-5">
                    <div className="text-3xl md:text-4xl font-semibold tracking-tight">{s.value}</div>
                    <div className="mt-2 text-xs uppercase tracking-[0.18em] text-ink-soft">{s.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Pain Points" title="What's actually failing today." />
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {detail.pains.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-background p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">0{i + 1}</div>
                  <div className="font-medium text-foreground">{p}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Key capabilities */}
      <section className="py-24">
        <div className="container-page">
          <SectionHeader
            eyebrow="Key Capabilities"
            title="What we engineer for this sector."
            description={detail.strategy}
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {detail.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <div className="group h-full rounded-2xl border border-border bg-background p-6 hover:border-border-strong hover:shadow-card transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] text-ink-soft tracking-widest">0{i + 1}</span>
                    <ArrowUpRight className="h-4 w-4 text-ink-soft group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <h3 className="mt-6 text-lg font-semibold tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{c.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Representative use cases */}
      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Representative Work" title="Where this shows up." />
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {detail.uses.map((u, i) => (
              <Reveal key={u} delay={i * 0.05}>
                <div className="flex items-center gap-4 rounded-xl border border-border bg-background p-5">
                  <div className="font-mono text-xs text-primary w-8">0{i + 1}</div>
                  <div className="font-medium">{u}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Common Mistakes"
              title="What to avoid in this sector."
              description="Patterns we see when planning starts late or systems are procured in isolation."
            />
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {detail.mistakes.map((m, i) => (
              <Reveal key={m} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm text-foreground leading-relaxed">{m}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="py-24">
          <div className="container-page grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHeader eyebrow="FAQ" title="Questions we hear from this sector." />
            </div>
            <div className="lg:col-span-7 grid gap-4">
              {faqs.map((f, i) => (
                <Reveal key={f.q} delay={i * 0.05}>
                  <details className="group rounded-xl border border-border bg-background p-5 open:border-border-strong open:shadow-card transition-all">
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-medium text-foreground">
                      <span>{f.q}</span>
                      <ArrowUpRight className="h-4 w-4 mt-1 text-ink-soft group-open:rotate-90 transition-transform" />
                    </summary>
                    <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24">
        <div className="container-page flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-2xl text-balance">
              Planning a {industry.title.toLowerCase()} program?
            </h2>
            <p className="mt-3 text-ink-soft">Share your drawings, project stage and technology expectations — a sector-specific review across security, automation, AV and intelligent building systems.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`mailto:arif@arifkhanglobal.com?subject=${encodeURIComponent(`Let's Talk — ${industry.title} Requirement`)}`}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:shadow-elevated transition-shadow"
            >
              Email Your Requirement <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-background px-7 py-3.5 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
            >
              Let's Talk
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
